import React from 'react';
import styled from 'styled-components';
import { Flex, Modal, Progress, Tag, Typography } from 'antd';
import {
  ArrowRightOutlined,
  ClockCircleTwoTone,
  CheckCircleTwoTone,
} from '@ant-design/icons';

const StyledSetupProgressModal = styled(Modal)`
  .ant-v5-modal-content {
    padding: 0;
    border-radius: 1.5rem;
    overflow: hidden;
  }

  .ant-v5-modal-footer {
    padding: 2rem 2.25rem;
  }

  .ant-v5-modal-header {
    margin-bottom: 0;
    padding: 1.5rem 2rem;
    background: transparent;
    border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorder};
  }
`;

const StyledList = styled(Flex)``;
const StyledListItem = styled(Flex)`
  cursor: pointer;
  padding: 1.25rem 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorder};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }
`;

const StyledListTag = styled(Tag)`
  font-size: 10px;
  padding: 0 0.5rem;
  display: flex;
  line-height: normal;
  align-items: center;
`;

const StyledListStatusSuccessIcon = styled(CheckCircleTwoTone)`
  font-size: 2.75rem;
`;
const StyledListStatusPendingIcon = styled(ClockCircleTwoTone)`
  font-size: 2.75rem;
`;

const StyledListStatusOptionalIcon = styled(ClockCircleTwoTone)`
  font-size: 2.75rem;
`;

const StyledListArrowIcon = styled(ArrowRightOutlined)`
  font-size: 1.5rem;
  color: rgba(0, 0, 0, 0.45) !important;
`;

type SetupProgressModalProps = {
  open: boolean;
  onCloseClick: () => void;
};

export function SetupProgressModal({
  open = false,
  onCloseClick,
}: SetupProgressModalProps) {
  return (
    <StyledSetupProgressModal
      centered
      open={open}
      footer={null}
      onCancel={onCloseClick}
      title={
        <Flex vertical gap={5}>
          <Flex gap={10} align="center" justify="flex-start">
            <Progress
              type="circle"
              percent={10}
              strokeColor="#00c2d1"
              size={25}
              format={() => null}
            />
            <Typography.Title level={4} style={{ margin: 0 }}>
              Your Progress
            </Typography.Title>
          </Flex>
          <Flex>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, fontWeight: 400 }}
            >
              You’ve completed 4 of 6 tasks
            </Typography.Text>
          </Flex>
        </Flex>
      }
    >
      <StyledList vertical gap={0}>
        <StyledListItem>
          <Flex
            gap={10}
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Flex gap={10} align="center">
              <StyledListStatusSuccessIcon twoToneColor="#00d0be" />
              <Flex vertical gap={2}>
                <Flex gap={10}>
                  <Typography.Text strong>Create Your Services</Typography.Text>
                  <StyledListTag color="#00d0be" bordered>
                    &#10024; AI
                  </StyledListTag>
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Please update your service. You can create more then one
                  service.
                </Typography.Text>
              </Flex>
            </Flex>
            <StyledListArrowIcon />
          </Flex>
        </StyledListItem>
        <StyledListItem>
          <Flex
            gap={10}
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Flex gap={10} align="center">
              <StyledListStatusPendingIcon twoToneColor="#a2a2a2" />
              <Flex vertical gap={2}>
                <Flex gap={10}>
                  <Typography.Text strong>Create Your Address</Typography.Text>
                  <StyledListTag color="#00d0be" bordered>
                    &#10024; AI
                  </StyledListTag>
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Please update your address and fill state/province. This is
                  important to be bookable in Google.
                </Typography.Text>
              </Flex>
            </Flex>
            <StyledListArrowIcon />
          </Flex>
        </StyledListItem>
        <StyledListItem>
          <Flex
            gap={10}
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Flex gap={10} align="center">
              <StyledListStatusPendingIcon twoToneColor="#a2a2a2" />
              <Flex vertical gap={2}>
                <Flex gap={10}>
                  <Typography.Text strong>
                    Create Your First Appointment
                  </Typography.Text>
                  <StyledListTag color="#00d0be" bordered>
                    &#10024; AI
                  </StyledListTag>
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Receive or create at least one appointment to see how your
                  calendar looks.
                </Typography.Text>
              </Flex>
            </Flex>
            <StyledListArrowIcon />
          </Flex>
        </StyledListItem>
        <StyledListItem>
          <Flex
            gap={10}
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Flex gap={10} align="center">
              <StyledListStatusPendingIcon twoToneColor="#a2a2a2" />
              <Flex vertical gap={2}>
                <Flex gap={10}>
                  <Typography.Text strong>
                    Choose Your Subscription
                  </Typography.Text>
                  <StyledListTag color="#00d0be" bordered>
                    &#10024; AI
                  </StyledListTag>
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Choose a subscription and complete your payment.
                </Typography.Text>
              </Flex>
            </Flex>
            <StyledListArrowIcon />
          </Flex>
        </StyledListItem>
        <StyledListItem>
          <Flex
            gap={10}
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Flex gap={10} align="center">
              <StyledListStatusOptionalIcon twoToneColor="#8bbefc" />
              <Flex vertical gap={2}>
                <Flex gap={10}>
                  <Typography.Text strong>Add Shore Pay Online</Typography.Text>
                  <StyledListTag color="blue" bordered>
                    Optional
                  </StyledListTag>
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Shore Pay Online lets customers pay when booking.
                </Typography.Text>
              </Flex>
            </Flex>
            <StyledListArrowIcon />
          </Flex>
        </StyledListItem>
      </StyledList>
    </StyledSetupProgressModal>
  );
}
